import { useSettings } from '../../hooks/queries'

/**
 * Ort und Wetter: ist es eingerichtet?
 *
 * Beim Einrichten fehlte genau diese Auskunft. Der Server weiß es und sagt es
 * auch (`/api/settings` → `services`), aber zu sehen war es nirgends — der
 * Weg war, die Adresse von Hand im Browser aufzurufen und im JSON
 * nachzusehen. Genau die Art Umweg, die diese Anwendung sonst vermeidet:
 * ausgeliefert wird per FTP, die `.env` bearbeitet ein Mensch mit einem
 * Texteditor, und ob ein Tippfehler darin steht, soll die Anwendung sagen.
 *
 * **Die Karte zeigt sich nur der Verwaltung** — und zwar daran erkennbar,
 * dass der Server die Decke mitschickt, nicht weil die Oberfläche aus der
 * Rolle schließt. Dasselbe Muster wie in `LimitsCard`.
 *
 * **„Nicht eingerichtet" ist keine Fehlermeldung.** Ohne die Dienste läuft
 * alles weiter; ein Vorschlag aus der Route heißt dann eben „Aufenthalt,
 * 2 Std." statt „Trondheim". Das steht auch so da — sonst sucht jemand einen
 * Fehler, den es nicht gibt.
 */
export function DiensteCard() {
  const settings = useSettings()
  const daten = settings.data

  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat entschieden.
  if (!daten?.ceiling) return null

  const dienste = daten.services ?? { place: false, weather: false }
  const beides = dienste.place && dienste.weather
  const nichts = !dienste.place && !dienste.weather

  return (
    <div className="card" style={{ margin: '0 auto 16px', boxShadow: 'none' }}>
      <h1 style={{ fontSize: 17 }}>Ort und Wetter</h1>
      <p className="card__lead" style={{ marginBottom: 10 }}>
        Was ein Punkt auf der Karte über sich sagen kann — der Ortsname und das
        Wetter zu der Stunde, in der du dort warst.
      </p>

      <p className="dienst">
        <span className="dienst__name">Ortsnamen</span>
        <Stand an={dienste.place} />
      </p>
      <p className="dienst">
        <span className="dienst__name">Wetter</span>
        <Stand an={dienste.weather} />
      </p>

      {nichts ? (
        <p className="card__lead" style={{ marginTop: 10, marginBottom: 0 }}>
          Das ist in Ordnung: ohne diese Dienste läuft alles weiter. Ein
          Vorschlag aus der Route heißt dann „Aufenthalt, 2 Std." statt nach
          seinem Ort. Wer sie einrichten möchte, findet die Schritte in der
          Installationsanleitung.
        </p>
      ) : !beides ? (
        <p className="card__lead" style={{ marginTop: 10, marginBottom: 0 }}>
          Eines von beiden fehlt. Nachzusehen ist in der <code>.env</code> auf
          dem Server:
        </p>
      ) : (
        <p className="card__lead" style={{ marginTop: 10, marginBottom: 0 }}>
          Beides ist da. Die Angaben werden geholt, sobald du eine Karte
          öffnest — einmal je Highlight, nie beim Anzeigen.
        </p>
      )}

      {!beides && !nichts && (
        <ul className="dienst__zeilen">
          <li>APPLE_TEAM_ID</li>
          <li>APPLE_MAPS_KEY_ID</li>
          <li>APPLE_WEATHER_KEY_ID</li>
          <li>APPLE_WEATHER_SERVICE_ID</li>
          <li>APPLE_PRIVATE_KEY_PATH</li>
        </ul>
      )}

      {!beides && (
        // Kein Neustart nötig -- hier läuft kein Prozess durch, der Server
        // liest die Datei bei jeder Anfrage neu. Die Oberfläche hält den Wert
        // allerdings zehn Minuten im Zwischenspeicher.
        <p className="dienst__hinweis">
          Der Server liest die <code>.env</code> bei jeder Anfrage neu — ein
          Neustart ist nicht nötig. Diese Seite zeigt den Stand nach dem
          nächsten Neuladen.
        </p>
      )}
    </div>
  )
}

function Stand({ an }: { an: boolean }) {
  return (
    <span className={an ? 'dienst__stand dienst__stand--an' : 'dienst__stand'}>
      {an ? 'eingerichtet' : 'nicht eingerichtet'}
    </span>
  )
}
